import { useMutation } from "@apollo/client";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useEffect, useState } from "react";

import { CREATE_PRO_HOME_SUBSCRIPTION_CHECKOUT } from "../../api/gqlQueries";
import { ReactComponent as Close } from "../../img/Close.svg";
import { ReactComponent as Sparkle } from "../../img/Sparkle.svg";

interface EnrollBrilliantMaxModalProps {
  closeEnrollBrilliantMaxModal: () => void;
  homeId: string;
  homeName: string;
  orgId: string;
  source: "home_details" | "unit_card";
}

const EnrollBrilliantMaxModal: React.FC<EnrollBrilliantMaxModalProps> = ({
  closeEnrollBrilliantMaxModal,
  homeId,
  homeName,
  orgId,
  source,
}) => {
  const [createProHomeSubscriptionCheckout] = useMutation(
    CREATE_PRO_HOME_SUBSCRIPTION_CHECKOUT
  );

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    mixpanel.track("Brilliant Max Pro Enroll Start", {
      home_id: homeId,
      source,
    });
  }, []);

  const handleProceedToCheckout = async () => {
    mixpanel.track("Brilliant Max Pro Checkout Started", {
      home_id: homeId,
    });
    setHasError(false);
    // Open the tab synchronously inside the click handler so it isn't blocked
    // by the popup blocker, then point it at the URL once the mutation returns.
    // Note: passing "noopener" makes window.open return null, so we null out
    // opener manually instead to keep the reference while staying secure.
    const newTab = window.open("", "_blank");
    if (newTab) {
      newTab.opener = null;
    }
    try {
      const { data } = await createProHomeSubscriptionCheckout({
        variables: { homeId, orgId },
      });
      const url = data?.createProHomeSubscriptionCheckout?.checkoutUrl;
      if (newTab && url) {
        newTab.location.href = url;
      } else {
        newTab?.close();
        setHasError(true);
      }
    } catch {
      newTab?.close();
      setHasError(true);
    }
  };

  return (
    <div className="popup-background">
      <div className="modal-create-container">
        <div className="modal-header">
          <div className="modal-title-container">
            <Sparkle className="cancel-bmx-modal-sparkle" title="Sparkle" />
            <div className="unit-title">
              Enroll in Brilliant Max:{" "}
              <span className="cancel-bmx-modal-title-name">{homeName}</span>
            </div>
          </div>
          <Close
            className="modal-header-img"
            onClick={closeEnrollBrilliantMaxModal}
            title="Close modal"
          />
        </div>

        <div className="modal-create-body">
          <div className="cancel-bmx-modal-info-card">
            You will be charged today $69.99 for the annual Brilliant Max plan
            for this Home, which will automatically renew in 1 year unless you
            cancel.
          </div>

          <div className="enroll-bmx-modal-table-2col">
            <span className="enroll-bmx-modal-table-header sub-heading">
              Home to be Subscribed
            </span>
            <span className="enroll-bmx-modal-table-header sub-heading">
              Next Billing Date
            </span>

            <span className="enroll-bmx-modal-table-value tool-tip">
              {homeName}
            </span>
            <span className="enroll-bmx-modal-table-date tool-tip">
              {moment().add(1, "year").format("LL")}
            </span>
          </div>

          {hasError && (
            <p className="enroll-bmx-modal-error">
              Something went wrong. Please try again.
            </p>
          )}

          <div className="cancel-bmx-modal-actions">
            <button
              className="tertiary-btn-small"
              type="button"
              onClick={closeEnrollBrilliantMaxModal}
            >
              Go Back
            </button>
            <button
              className="tertiary-btn-small tertiary-btn-small-main"
              type="button"
              onClick={handleProceedToCheckout}
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrollBrilliantMaxModal;
